import { Link } from "react-router-dom";
import { blogPosts } from "@/data/blogPosts";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEOHead } from "@/components/SEOHead";

export default function BlogIndex() {
  return (
    <>
      <SEOHead
        title="Ratgeber & Blog | Miros Haushaltsauflösung Berlin"
        description="Hilfreiche Tipps, Checklisten und Ratgeber rund um die Themen Haushaltsauflösung, Entrümpelung und Nachlassverwertung in Berlin."
        canonical="https://miros-haushaltsaufloesung.de/ratgeber"
      />
      <div className="flex flex-col w-full">
        {/* Hero Section */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-[#0c2340] text-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-black mb-6 leading-tight">
                Ratgeber & <span className="text-accent">Wissenswertes</span>
              </h1>
              <p className="text-lg md:text-xl text-white/80 leading-relaxed max-w-2xl">
                Hilfreiche Tipps, Checklisten und Expertenwissen rund um die
                Themen Haushaltsauflösung, Entrümpelung und Nachlassverwertung
                in Berlin.
              </p>
            </div>
          </div>
        </section>

        {/* Blog Grid Section */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <article
                  key={post.id}
                  className="bg-card rounded-sm border border-border shadow-sm hover:shadow-md transition-all flex flex-col overflow-hidden group"
                >
                  <Link
                    to={`/ratgeber/${post.slug}`}
                    className="relative h-56 overflow-hidden block"
                  >
                    <div className="absolute top-4 left-4 z-10 bg-accent text-accent-foreground px-3 py-1 text-xs font-bold uppercase rounded-sm">
                      {post.category}
                    </div>
                    <img
                      src={post.imageUrl}
                      alt={post.title}
                      width="400"
                      height="250"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                  </Link>
                  <div className="p-6 md:p-8 flex flex-col flex-grow">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>{post.date}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    <h2 className="text-xl font-bold text-foreground mb-3 line-clamp-2 group-hover:text-primary transition-colors">
                      <Link to={`/ratgeber/${post.slug}`}>{post.title}</Link>
                    </h2>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-6 flex-grow line-clamp-3">
                      {post.excerpt}
                    </p>
                    <Link
                      to={`/ratgeber/${post.slug}`}
                      className="inline-flex items-center text-primary font-bold hover:text-accent transition-colors mt-auto"
                    >
                      Weiterlesen <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-secondary/30">
          <div className="container mx-auto px-4 md:px-6 text-center max-w-3xl">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground mb-6">
              Benötigen Sie persönliche Beratung?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Wir stehen Ihnen bei Ihrer Haushaltsauflösung in Berlin
              professionell zur Seite. Rufen Sie uns an oder fordern Sie ein
              kostenloses Angebot an.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90"
                asChild
              >
                <Link to="/kontakt">
                  Kostenfreies Angebot <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-14 px-8 text-lg font-bold border-border bg-background hover:bg-secondary"
                asChild
              >
                <a href="tel:015253181061">01525 3181061 anrufen</a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
